import {
  Action,
  ActionReducer,
  ActionReducerMap,
  createFeatureSelector,
  MetaReducer,
} from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { InjectionToken } from '@angular/core';

import * as fromCarbonImpact from './carbon-impact.reducer';
import * as fromCost from './cost.reducer';
import * as fromInputs from './inputs.reducer';
import * as fromShared from './shared.reducer';

/**
 * We treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface State {
  [fromCarbonImpact.carbonImpactFeatureKey]: fromCarbonImpact.State;
  [fromCost.costFeatureKey]: fromCost.State;
  [fromInputs.inputsFeatureKey]: fromInputs.State;
  [fromShared.sharedFeatureKey]: fromShared.State;
}

/**
 * Our state is composed of a map of action reducer functions.
 * These reducer functions are called with each dispatched action
 * and the current or initial state and return a new immutable state.
 */
export const ROOT_REDUCERS = new InjectionToken<
  ActionReducerMap<State, Action>
>('Root reducers token', {
  factory: () => ({
    [fromCarbonImpact.carbonImpactFeatureKey]: fromCarbonImpact.reducer,
    [fromCost.costFeatureKey]: fromCost.reducer,
    [fromInputs.inputsFeatureKey]: fromInputs.reducer,
    [fromShared.sharedFeatureKey]: fromShared.reducer,
  }),
});

// console.log all actions
export function logger(reducer: ActionReducer<State>): ActionReducer<State> {
  return (state, action) => {
    const result = reducer(state, action);
    console.groupCollapsed(action.type);
    console.log('prev state', state);
    console.log('action', action);
    console.log('next state', result);
    console.groupEnd();

    return result;
  };
}

/**
 * By default, @ngrx/store uses combineReducers with the reducer map to compose
 * the root meta-reducer. To add more meta-reducers, provide an array of meta-reducers
 * that will be composed to form the root meta-reducer.
 */
export const metaReducers: MetaReducer<State>[] = !environment.production
  ? [logger]
  : [];

/**
 * Feature Selectors
 */
export const carbonImpactState = createFeatureSelector<
  State,
  fromCarbonImpact.State
>(fromCarbonImpact.carbonImpactFeatureKey);
export const costState = createFeatureSelector<State, fromCost.State>(
  fromCost.costFeatureKey
);
export const inputsState = createFeatureSelector<State, fromInputs.State>(
  fromInputs.inputsFeatureKey
);
export const sharedState = createFeatureSelector<State, fromShared.State>(
  fromShared.sharedFeatureKey
);
